import { ApiResponse } from 'apisauce';

import api from '@/shared/config/http.config';
import { ErrorInfo } from '@/shared/interfaces/error-info.interface';
import { ResponseModel } from '@/shared/interfaces/response-model.interface';
import UserMapper from '@/shared/mappers/user.mapper';
import { AddUserDataModel, UserModel } from '@/shared/models/user.model';

import {
  UsersPayload,
  UsersListResponse,
  UserResponse,
  CreateUserResponse,
  UpdateUserResponse,
  UpdateUserImageResponse,
  SignedUrlResponse,
  UserActivitiesListResponse,
  UserActivitiesPayload
} from '../types';

function throwIfNotOk<T>(
  response: ApiResponse<ResponseModel<T>, ErrorInfo>,
  errorMessage: string
): asserts response is ApiResponse<ResponseModel<T>, ErrorInfo> & { ok: true } {
  if (!response.ok) {
    const backendMessage =
      response.data?.message ||
      response.data?.errorMessage ||
      (Array.isArray(response.data?.errors) ? response.data?.errors?.[0] : undefined);

    throw new Error(
      backendMessage ||
        (response.data?.errorCode ? String(response.data.errorCode) : undefined) ||
        response.problem ||
        errorMessage
    );
  }
}
export const usersApiService = {
  async getAllUsers(payload: UsersPayload): Promise<UsersListResponse> {
    const response = await api.post<ResponseModel<UserModel[]>, ErrorInfo>(
      '/users/search',
      payload
    );

    throwIfNotOk(response, 'Failed to fetch users');

    const responseData = response.data;
    const usersData = responseData?.data || [];

    const mappedUsers = UserMapper.mapToUIList(usersData);

    return {
      data: mappedUsers,
      total_count: responseData?.total_count || 0
    };
  },

  async getUserById(id: string): Promise<UserResponse> {
    const response = await api.get<ResponseModel<UserModel>, ErrorInfo>(`/users/${id}`);

    throwIfNotOk(response, 'Failed to fetch user');

    const userData = response.data?.data;

    if (!userData) {
      throw new Error('User data not found in response');
    }

    const mappedUser = UserMapper.mapToUI(userData);
    return { data: mappedUser };
  },

  async createUser(userData: AddUserDataModel): Promise<CreateUserResponse> {
    const response = await api.post<ResponseModel<UserModel>, ErrorInfo>(
      '/users/addUser',
      userData
    );

    throwIfNotOk(response, 'Failed to create user');

    const createdUserData = response.data?.data;

    if (!createdUserData) {
      throw new Error('Created user data not found in response');
    }

    const mappedUser = UserMapper.mapToUI(createdUserData);
    return { data: mappedUser };
  },

  async updateUser(userData: AddUserDataModel): Promise<UpdateUserResponse> {
    const { userId, ...updateData } = userData;

    const response = await api.put<ResponseModel<UserModel>, ErrorInfo>(
      `/users/${userId}`,
      updateData
    );

    throwIfNotOk(response, 'Failed to update user');

    const updatedUserData = response.data?.data;

    if (!updatedUserData) {
      throw new Error('Updated user data not found in response');
    }

    const mappedUser = UserMapper.mapToUI(updatedUserData);
    return { data: mappedUser };
  },

  async updateUserImage(id: number, imageData: FormData): Promise<UpdateUserImageResponse> {
    // Ensure the ID is included in the FormData
    if (!imageData.has('id')) {
      imageData.append('id', id.toString());
    }

    const response = await api.put<ResponseModel<UserModel>, ErrorInfo>(`/users/image`, imageData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    throwIfNotOk(response, 'Failed to update user image');

    const updatedUserData = response.data?.data;
    if (!updatedUserData) {
      throw new Error('Updated user image data not found in response');
    }

    const mappedUser = UserMapper.mapToUI(updatedUserData);
    return { data: mappedUser };
  },

  async getSignedUrl(key: string): Promise<SignedUrlResponse> {
    // Validate that key is provided and not empty
    if (!key || key.trim() === '') {
      throw new Error('Key parameter is required and cannot be empty');
    }

    const response = await api.post<ResponseModel<SignedUrlResponse>, ErrorInfo>(
      `/users/getSignedUrl`,
      {
        key: key.trim()
      }
    );

    throwIfNotOk(response, 'Failed to get signed URL');

    const signedUrlData = response.data?.data;
    if (!signedUrlData) {
      throw new Error('Signed URL data not found in response');
    }

    return signedUrlData;
  },

  async deleteUserImage(id: string, key: string): Promise<void> {
    const response = await api.delete<void, ErrorInfo>(`/users/image`, {}, { data: { id, key } });

    throwIfNotOk(response, 'Failed to delete user image');
  },

  async getUserActivities(payload: UserActivitiesPayload): Promise<UserActivitiesListResponse> {
    const { userId, skip, limit } = payload;
    const response = await api.get<ResponseModel<UserActivitiesListResponse['data']>, ErrorInfo>(
      `/users/${userId}/activities`,
      { skip, limit }
    );

    throwIfNotOk(response, 'Failed to fetch user activities');

    const responseData = response.data;
    return {
      data: responseData?.data || [],
      total_count: responseData?.total_count || 0
    };
  },

  async unblockUser(userId: string): Promise<UpdateUserResponse> {
    const response = await api.put<ResponseModel<UserModel>, ErrorInfo>(`/users/${userId}`, {
      isPhoneSuspended: false
    });

    throwIfNotOk(response, 'Failed to unblock user');

    const updatedUserData = response.data?.data;
    if (!updatedUserData) {
      throw new Error('Updated user data not found in response');
    }

    const mappedUser = UserMapper.mapToUI(updatedUserData);
    return { data: mappedUser };
  },

  async unblockEmailLogin(userId: string): Promise<UserResponse> {
    const response = await api.put<ResponseModel<UserModel>, ErrorInfo>(
      `/users/${userId}/unblock-email-login`,
      {}
    );

    throwIfNotOk(response, 'Failed to unblock email login');

    const updatedUserData = response.data?.data;
    if (!updatedUserData) {
      throw new Error('Updated user data not found in response');
    }

    const mappedUser = UserMapper.mapToUI(updatedUserData);
    return { data: mappedUser };
  }
};
